import { Flex } from "@chakra-ui/react"
import { useCallback, useState } from "react"
import { useIntl } from "react-intl"

import { inverseColors } from "@/theme/values"

import { AngleLeftIcon, AngleRightIcon } from "../Icons"
import { MenuToggle } from "./MenuItem"
import MenuItems from "./MenuItems"
import { MenuLogo } from "./MenuLogo"
import MobileMenuBar from "./MobileMenuBar"

const COLLAPSED_STORAGE_KEY = "menuCollapsed"

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true"
  } catch {
    return false
  }
}

const storeCollapsed = (collapsed: boolean) => {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed))
  } catch {
    // Storage unavailable (private mode): the choice is just not remembered.
  }
}

// Application sidebar of the BARNES design system (sidebar.css): black rail (inverse theme),
// 256px expanded / 72px collapsed, active item marked by a 2px red rule. Below the lg breakpoint
// the rail gives way to a top bar opening the same entries in an off-screen panel.
export default function Menu() {
  const intl = useIntl()
  const [collapsed, setCollapsed] = useState(readCollapsed)

  const toggleCollapsed = useCallback(() => {
    setCollapsed(previous => {
      storeCollapsed(!previous)
      return !previous
    })
  }, [])

  return (
    <>
      <Flex
        as="nav"
        aria-label={intl.formatMessage({ id: "menu.label" })}
        display={{ base: "none", lg: "flex" }}
        flexDir="column"
        bg="surfaceInverse"
        color={inverseColors.text}
        w={collapsed ? "sidebarWCollapsed" : "sidebarW"}
        flexShrink={0}
        transition="width 240ms cubic-bezier(0.22, 0.61, 0.36, 1)"
        data-collapsed={collapsed}
        data-testid="menu-rail"
      >
        {/* 72px seal; 48px on the collapsed rail, 72px wide. */}
        <Flex
          flex="none"
          h="pageHeaderH"
          align="center"
          justify="center"
          px={collapsed ? "3" : "6"}
        >
          <MenuLogo size={collapsed ? "48px" : "72px"} />
        </Flex>
        <MenuItems
          collapsed={collapsed}
          footer={
            <MenuToggle
              collapsed={collapsed}
              icon={collapsed ? AngleRightIcon : AngleLeftIcon}
              label={intl.formatMessage({ id: collapsed ? "menu.expand" : "menu.collapse" })}
              onClick={toggleCollapsed}
            />
          }
        />
      </Flex>
      <MobileMenuBar />
    </>
  )
}
